import React, { useState, useRef, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Strike from "@tiptap/extension-strike";
import Heading from "@tiptap/extension-heading";
import CodeBlock from "@tiptap/extension-code-block";
import Blockquote from "@tiptap/extension-blockquote";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import HardBreak from "@tiptap/extension-hard-break";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Image from "@tiptap/extension-image";

import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaQuoteRight,
  FaCode,
  FaUndo,
  FaRedo,
  FaStrikethrough,
  FaSubscript,
  FaSuperscript,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaImage,
  FaLink,
  FaOutdent,
  FaIndent,
} from "react-icons/fa";

import "./styles.css";
import FontFamily from "./extensions/FontFamily";

const TipTapEditor = ({ content, onChange }) => {
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const fileInputRef = useRef();

  // console.log('Faq content value:',content)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        strike: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        hardBreak: false,
      }),
      TextStyle,
      FontFamily,
      Underline,
      Strike,
      Link,
      Subscript,
      Superscript,
      Image,
      Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
      // CodeBlock,
      // Blockquote,
      // HorizontalRule,
      // HardBreak,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content,
    onUpdate: ({ editor }) => {
      // console.log("Registered commands:", Object.keys(editor.commands));
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "<p></p>");
    }
  }, [content, editor]);

  const handleInsertImage = async () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setShowImageModal(false);
      return;
    }

    const file = fileInputRef.current?.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await fetch("https://your-upload-api.com/upload", {
          method: "POST",
          body: formData,
        });
        const data = await response.json();
        if (data?.url) {
          editor.chain().focus().setImage({ src: data.url }).run();
        } else {
          alert("Upload failed");
        }
      } catch (err) {
        alert("Image upload error");
        console.error(err);
      }
      setShowImageModal(false);
    }
  };

  const addLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = prompt("Enter link URL", previousUrl || "");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    }
  };

  if (!editor) return null;

  const btnClass = (name, attrs = {}) => {
    if (name) {
      return `btn btn-unstyled ${
        editor.isActive(name, attrs) ? "text-primary" : ""
      }`;
    }
    return "btn btn-unstyled";
  };

  const formattingButtons = [
    { icon: FaBold, command: "toggleBold" },
    { icon: FaItalic, command: "toggleItalic" },
    { icon: FaUnderline, command: "toggleUnderline" },
    { icon: FaStrikethrough, command: "toggleStrike" },
    { icon: FaSubscript, command: "toggleSubscript" },
    { icon: FaSuperscript, command: "toggleSuperscript" },
  ];

  const getActiveHeading = () => {
    for (let i = 1; i <= 6; i++) {
      if (editor.isActive("heading", { level: i })) {
        return String(i);
      }
    }

    if (editor.isActive("paragraph")) {
      return "p";
    }

    if (editor.isActive("textStyle", { class: "subtitle" })) {
      return "subtitle";
    }

    return "p";
  };

  const handleHeadingChange = (e) => {
    const value = e.target.value;

    if (value === "p") {
      editor.chain().focus().setParagraph().run();
    } else if (value === "subtitle") {
      editor
        .chain()
        .focus()
        .setParagraph()
        .setMark("textStyle", { class: "subtitle" })
        .run();
    } else {
      editor
        .chain()
        .focus()
        .setHeading({ level: Number(value) })
        .run();
    }
  };

  return (
    <div className="editor-container border rounded p-3 bg-white">
      <div className="toolbar mb-3 d-flex flex-wrap gap-3 align-items-center">
        {/* Font Controls */}
        <div className="d-flex gap-2">
          <select
            className="form-select form-select-sm pe-4"
            value={getActiveHeading()}
            onChange={handleHeadingChange}
          >
            <option value="p">Normal</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
            <option value="5">Heading 5</option>
            <option value="6">Heading 6</option>
            <option value="subtitle">Subtitle</option>
          </select>

          <select
            className="form-select form-select-sm pe-4"
            onChange={(e) =>
              editor.chain().focus().setFontFamily(e.target.value).run()
            }
          >
            <option value="">Font</option>
            <option value="Arial, sans-serif">Sans</option>
            <option value="'Times New Roman', serif">Serif</option>
            <option value="'Courier New', monospace">Mono</option>
          </select>
        </div>

        {/* Text Formatting */}
        <div className="d-flex gap-1">
          {formattingButtons.map(({ icon: Icon, command }, i) => (
            <button
              type="button"
              key={i}
              className={btnClass()}
              onClick={() => editor.chain().focus()[command]().run()}
            >
              <Icon />
            </button>
          ))}
        </div>

        {/* Lists & Indentation */}
        <div className="d-flex gap-1">
          <button
            type="button"
            className={btnClass("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FaListUl />
          </button>
          <button
            type="button"
            className={btnClass("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FaListOl />
          </button>
          <button
            type="button"
            className="btn btn-unstyled"
            onClick={() => editor.chain().focus().lift().run()}
          >
            <FaOutdent />
          </button>
          <button
            type="button"
            className="btn btn-unstyled"
            onClick={() =>
              editor.chain().focus().sinkListItem("listItem").run()
            }
          >
            <FaIndent />
          </button>
        </div>

        {/* Block Elements */}
        <div className="d-flex gap-1">
          <button
            type="button"
            className={btnClass("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <FaQuoteRight />
          </button>
          <button
            type="button"
            className={btnClass("codeBlock")}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <FaCode />
          </button>
        </div>

        {/* Media & Links */}
        {/* <div className="d-flex gap-1">
          <button type="button" className={btnClass("link")} onClick={addLink}>
            <FaLink />
          </button>
          <button
            type="button"
            className="btn btn-unstyled"
            onClick={() => setShowImageModal(true)}
          >
            <FaImage />
          </button>
        </div> */}

        {/* Alignment */}
        <div className="d-flex gap-1">
          <button
            type="button"
            className={btnClass(null, { textAlign: "left" })}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <FaAlignLeft />
          </button>
          <button
            type="button"
            className={btnClass(null, { textAlign: "center" })}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <FaAlignCenter />
          </button>
          <button
            type="button"
            className={btnClass(null, { textAlign: "right" })}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <FaAlignRight />
          </button>
        </div>

        {/* Undo/Redo */}
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn btn-unstyled"
            onClick={() => editor.chain().focus().undo().run()}
          >
            <FaUndo />
          </button>
          <button
            type="button"
            className="btn btn-unstyled"
            onClick={() => editor.chain().focus().redo().run()}
          >
            <FaRedo />
          </button>
        </div>
      </div>

      <EditorContent editor={editor} />

      {/* Image Modal */}
      {showImageModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Insert Image</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImageModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label>Image URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div className="text-center">OR</div>
                <div className="mb-3">
                  <label>Upload Image</label>
                  <input
                    type="file"
                    className="form-control"
                    ref={fileInputRef}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImageModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleInsertImage}
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TipTapEditor;
